import { cn } from '../lib/cn'
import { SPRING_SNAPPY } from '../tokens/motion'
import { motion } from 'framer-motion'

/**
 * A decision between two states, drawn as the house draws it: no second hue,
 * the track carrying the state in its own weight. Off, the switch is a quiet
 * tint with a muted knob; on, the track is the foreground and the knob reads
 * as a hole in it. The knob travels on the panel spring, so the hand feels
 * the same motion the column uses.
 *
 * A native checkbox styled as a switch was the lazy shape; this is a real
 * `role="switch"` — Space commits, Escape never surprises it, and the state
 * is announced.
 */
export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  /** The accessible name — what is being switched. */
  label: string
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border transition-colors duration-fast outline-none',
        'focus-visible:border-foreground/60',
        checked ? 'border-transparent bg-foreground' : 'border-hairline bg-tint',
        disabled && 'cursor-not-allowed opacity-45',
      )}
    >
      <motion.span
        aria-hidden
        transition={SPRING_SNAPPY}
        animate={{ x: checked ? 20 : 0 }}
        className={cn(
          'absolute left-[3px] size-4 rounded-full',
          checked ? 'bg-surface' : 'bg-muted-foreground/70',
        )}
      />
    </button>
  )
}
